import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus } from 'lucide-react';

import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  API,
  UNIT_OPTIONS,
  bucketLabel,
  getStatusBadgeClass,
} from '../lib/componentsModule';

const emptyTx = {
  transaction_type: 'consume',
  quantity: 1,
  unit: 'units',
  transaction_date: new Date().toISOString().split('T')[0],
  notes: '',
};

export default function ComponentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [component, setComponent] = useState(null);
  const [assets, setAssets] = useState([]);
  const [parentComponents, setParentComponents] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showTxDialog, setShowTxDialog] = useState(false);
  const [txForm, setTxForm] = useState(emptyTx);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [componentRes, assetsRes, transactionsRes, componentsRes] = await Promise.all([
        axios.get(`${API}/components/${id}`),
        axios.get(`${API}/assets`),
        axios.get(`${API}/components/${id}/transactions`),
        axios.get(`${API}/components?bucket=operational&limit=1000`),
      ]);

      setComponent(componentRes.data);
      setAssets(assetsRes.data || []);
      setTransactions(transactionsRes.data || []);
      setParentComponents(componentsRes.data?.items || []);
    } catch (error) {
      toast.error('Failed to load component details');
      navigate('/components');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddTransaction = async () => {
    if (Number(txForm.quantity) <= 0) {
      toast.error('Quantity must be greater than zero');
      return;
    }

    try {
      await axios.post(`${API}/components/${id}/transactions`, {
        ...txForm,
        quantity: Number(txForm.quantity),
        transaction_date: txForm.transaction_date ? new Date(`${txForm.transaction_date}T00:00:00.000Z`).toISOString() : undefined,
      });
      toast.success('Transaction added');
      setShowTxDialog(false);
      setTxForm(emptyTx);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add transaction');
    }
  };

  const assetName = useMemo(
    () => assets.find((asset) => asset.id === component?.asset_id)?.name || 'Unlinked asset',
    [assets, component]
  );

  const parentComponentName = useMemo(
    () => parentComponents.find((parent) => parent.id === component?.parent_component_id)?.name || 'None',
    [parentComponents, component]
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  if (!component) {
    return (
      <DashboardLayout>
        <div className="text-center py-12 text-slate-500">Component not found</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="outline" asChild>
              <Link to="/components">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Link>
            </Button>

            <div>
              <h1 className="text-2xl font-bold text-slate-900">{component.name}</h1>
              <p className="text-slate-500">{bucketLabel(component.bucket)}</p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/components/${component.id}/edit`)}>Edit</Button>
            <Button onClick={() => setShowTxDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Transaction
            </Button>
          </div>
        </div>

        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="transactions">Logs</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader><CardTitle>Common</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                <div><p className="text-slate-500">Bucket</p><p className="font-medium">{bucketLabel(component.bucket)}</p></div>
                <div><p className="text-slate-500">Type</p><p className="font-medium">{component.type || 'N/A'}</p></div>
                <div>
                  <p className="text-slate-500">Status</p>
                  {component.status ? (
                    <Badge variant="outline" className={getStatusBadgeClass(component.bucket, component.status)}>{component.status.replaceAll('_', ' ')}</Badge>
                  ) : (
                    <p className="font-medium">N/A</p>
                  )}
                </div>
                <div><p className="text-slate-500">Manufacturer</p><p className="font-medium">{component.manufacturer || 'N/A'}</p></div>
                <div><p className="text-slate-500">Model</p><p className="font-medium">{component.model || 'N/A'}</p></div>
                <div><p className="text-slate-500">Serial Number</p><p className="font-medium">{component.serial_number || 'N/A'}</p></div>
                <div><p className="text-slate-500">Quantity</p><p className="font-medium">{Number(component.quantity || 0).toLocaleString()} {component.unit}</p></div>
                <div><p className="text-slate-500">Asset</p><p className="font-medium">{assetName}</p></div>
                <div><p className="text-slate-500">Parent Component</p><p className="font-medium">{parentComponentName}</p></div>
                <div className="md:col-span-2 lg:col-span-3"><p className="text-slate-500">Notes</p><p className="font-medium">{component.notes || 'N/A'}</p></div>
                <div className="md:col-span-2 lg:col-span-3"><p className="text-slate-500">OEM Datasheet</p>{component.oem_datasheet_url ? <a href={component.oem_datasheet_url} target="_blank" rel="noreferrer" className="text-sky-600 underline">Open link</a> : <p className="font-medium">N/A</p>}</div>
              </CardContent>
            </Card>

            {component.bucket === 'operational' && (
              <Card>
                <CardHeader><CardTitle>Operational Fields</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  <div><p className="text-slate-500">Installation Date</p><p className="font-medium">{component.installation_date || 'N/A'}</p></div>
                  <div><p className="text-slate-500">Warranty Expiry Date</p><p className="font-medium">{component.warranty_expiry_date || 'N/A'}</p></div>
                  <div><p className="text-slate-500">Last Preventive Maintenance Date</p><p className="font-medium">{component.last_pm_date || 'N/A'}</p></div>
                  <div><p className="text-slate-500">Next PM</p><p className="font-medium">{component.next_pm_date || 'N/A'}</p></div>
                  <div><p className="text-slate-500">Tilt</p><p className="font-medium">{component.tilt ?? 'N/A'}</p></div>
                  <div><p className="text-slate-500">Azimuth</p><p className="font-medium">{component.azimuth ?? 'N/A'}</p></div>
                  <div><p className="text-slate-500">DC Capacity (kW)</p><p className="font-medium">{component.dc_capacity_kw ?? 'N/A'}</p></div>
                  <div><p className="text-slate-500">AC Capacity (kW)</p><p className="font-medium">{component.ac_capacity_kw ?? 'N/A'}</p></div>
                </CardContent>
              </Card>
            )}

            {component.bucket === 'spare' && (
              <Card>
                <CardHeader><CardTitle>Spare Fields</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  <div><p className="text-slate-500">Safety Quantity</p><p className="font-medium">{component.safety_quantity ?? 'N/A'}</p></div>
                  <div><p className="text-slate-500">Expiry Date</p><p className="font-medium">{component.expiry_date || 'N/A'}</p></div>
                  <div><p className="text-slate-500">Manufacturing Date</p><p className="font-medium">{component.manufacturing_date || 'N/A'}</p></div>
                  <div><p className="text-slate-500">Last Maintenance Date</p><p className="font-medium">{component.last_maintenance_date || 'N/A'}</p></div>
                  <div><p className="text-slate-500">Next Maintenance Date</p><p className="font-medium">{component.next_maintenance_date || 'N/A'}</p></div>
                </CardContent>
              </Card>
            )}

            {component.bucket === 'scrap_recyclable' && (
              <Card>
                <CardHeader><CardTitle>Scrap / Recyclable Fields</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  <div><p className="text-slate-500">Recycled Date</p><p className="font-medium">{component.recycled_date || 'N/A'}</p></div>
                  <div><p className="text-slate-500">Rycycling Company</p><p className="font-medium">{component.recycled_name || 'N/A'}</p></div>
                  <div><p className="text-slate-500">Credited Amount</p><p className="font-medium">{component.amount_received != null ? `${component.amount_received} ${component.amount_currency || ''}` : 'N/A'}</p></div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="transactions">
            <Card>
              <CardHeader><CardTitle>Stock In / Consumption Log</CardTitle></CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <p className="text-slate-500">No transaction logs yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell>{new Date(tx.transaction_date || tx.created_at).toLocaleString()}</TableCell>
                          <TableCell className="capitalize">{(tx.transaction_type || '').replaceAll('_', ' ')}</TableCell>
                          <TableCell>{Number(tx.quantity || 0).toLocaleString()}</TableCell>
                          <TableCell>{tx.unit}</TableCell>
                          <TableCell>{tx.user_name || 'N/A'}</TableCell>
                          <TableCell>{tx.notes || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showTxDialog} onOpenChange={(open) => {
        setShowTxDialog(open);
        if (!open) setTxForm(emptyTx);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Transaction</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={txForm.transaction_type} onValueChange={(value) => setTxForm((prev) => ({ ...prev, transaction_type: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="consume">Consume</SelectItem>
                  <SelectItem value="stock_in">Stock In</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input type="number" value={txForm.quantity} onChange={(e) => setTxForm((prev) => ({ ...prev, quantity: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Select value={txForm.unit} onValueChange={(value) => setTxForm((prev) => ({ ...prev, unit: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIT_OPTIONS.map((unit) => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={txForm.transaction_date} onChange={(e) => setTxForm((prev) => ({ ...prev, transaction_date: e.target.value }))} />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={txForm.notes} onChange={(e) => setTxForm((prev) => ({ ...prev, notes: e.target.value }))} rows={3} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTxDialog(false)}>Cancel</Button>
            <Button onClick={handleAddTransaction}>Save Transaction</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
